import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import FormFieldError from "./FormFieldError";
import { useLocale } from "../lib/locale";
import { searchCities, SEARCH_DEBOUNCE_MS } from "../services/locationSearch";

const MIN_SEARCH_LEN = 2;

function citySubline(c) {
  const country = c.country_name || c.countryName;
  if (c.state) return [c.state, country].filter(Boolean).join(", ");
  return country || c.country_code || c.countryCode || "";
}

export function formatCitySubline(c) {
  if (!c) return "";
  const parts = citySubline(c);
  return c.flag ? `${c.flag} ${parts}` : parts;
}

export default function CityAutocomplete({
  value,
  onChange,
  placeholder = "Start typing a city…",
  testId,
  required = false,
  className = "field mt-2",
  onResolvedChange,
  commitOnly = false,
  hideSubline = false,
  inputId,
  error: externalError,
}) {
  const locale = useLocale();
  const listId = useId();
  const wrapRef = useRef(null);
  const requestSeq = useRef(0);
  const onResolvedChangeRef = useRef(onResolvedChange);
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [resolved, setResolved] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [searching, setSearching] = useState(false);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    onResolvedChangeRef.current = onResolvedChange;
  }, [onResolvedChange]);

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  const applyResolved = useCallback((city, { notify = true } = {}) => {
    setResolved(city);
    if (notify) onResolvedChangeRef.current?.(city);
  }, []);

  const runSearch = useCallback(
    async (q, { resolveExact = false } = {}) => {
      const seq = ++requestSeq.current;
      const typedQuery = (q || "").trim();
      if (typedQuery.length < MIN_SEARCH_LEN) {
        setSuggestions([]);
        setSearching(false);
        setPending(false);
        if (resolveExact) {
          applyResolved(null);
          setInvalid(false);
        }
        return [];
      }
      try {
        setPending(false);
        setSearching(true);
        const items = await searchCities({
          query: q || "",
          countryCode: typedQuery ? undefined : locale.country_code || undefined,
          limit: resolveExact ? 20 : 8,
        });
        if (seq !== requestSeq.current) return items;
        setSuggestions(items);
        if (resolveExact && typedQuery) {
          const match = items.find((c) => c.name.toLowerCase() === typedQuery.toLowerCase());
          applyResolved(match || null);
          setInvalid(!match);
        }
        return items;
      } catch {
        if (seq !== requestSeq.current) return [];
        setSuggestions([]);
        return [];
      } finally {
        if (seq === requestSeq.current) {
          setSearching(false);
          setPending(false);
        }
      }
    },
    [locale.country_code, applyResolved]
  );

  useEffect(() => {
    if (!open) return undefined;
    if (query.trim().length < MIN_SEARCH_LEN) {
      setSuggestions([]);
      setPending(false);
      return undefined;
    }
    setPending(true);
    const id = setTimeout(() => runSearch(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query, open, runSearch]);

  useEffect(() => {
    if (commitOnly || !value?.trim() || open || value.trim().length < MIN_SEARCH_LEN) return undefined;
    const id = setTimeout(() => runSearch(value, { resolveExact: true }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [value, open, runSearch, commitOnly]);

  useEffect(() => {
    if (value) return;
    setResolved(null);
    setInvalid(false);
    if (!commitOnly) onResolvedChangeRef.current?.(null);
  }, [value, commitOnly]);

  useEffect(() => {
    function onDocClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  function pick(city) {
    onChange(city.name);
    setQuery(city.name);
    applyResolved(city);
    setInvalid(false);
    setSuggestions([]);
    setPending(false);
    setOpen(false);
  }

  function handleChange(e) {
    const next = e.target.value;
    setQuery(next);
    onChange(next);
    setOpen(true);
    setInvalid(false);
    if (commitOnly) {
      setResolved(null);
    } else {
      applyResolved(null);
    }
    if (next.trim().length < MIN_SEARCH_LEN) {
      setSuggestions([]);
      setPending(false);
    }
  }

  function handleFocus() {
    setOpen(true);
  }

  function handleKeyDown(e) {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const trimmed = query.trim();
    if (trimmed.length < MIN_SEARCH_LEN) return;
    const exact = suggestions.find((c) => c.name.toLowerCase() === trimmed.toLowerCase());
    if (exact) pick(exact);
    else if (suggestions.length === 1) pick(suggestions[0]);
  }

  function handleBlur() {
    if (commitOnly) {
      window.setTimeout(() => setOpen(false), 140);
      return;
    }
    window.setTimeout(() => {
      setOpen(false);
      if (!query.trim() || query.trim().length < MIN_SEARCH_LEN) {
        setInvalid(false);
        return;
      }
      const exact = suggestions.find((c) => c.name.toLowerCase() === query.trim().toLowerCase());
      if (exact) pick(exact);
      else if (suggestions.length === 1 && query.trim().length >= 2) pick(suggestions[0]);
      else if (query.trim() !== (value || "").trim()) setInvalid(true);
    }, 140);
  }

  const showList = open && query.trim().length >= MIN_SEARCH_LEN;
  const waiting = pending || searching;
  const internalError =
    invalid && !showList && !externalError && !waiting
      ? "Select a city from the list."
      : null;
  const errorMessage = externalError || internalError;
  const errorId = inputId && errorMessage ? `${inputId}-error` : testId && errorMessage ? `${testId}-error` : undefined;

  return (
    <div className="relative" ref={wrapRef}>
      <input
        id={inputId}
        className={`${className}${errorMessage ? " ring-2 ring-red-500/30 border-red-400/60" : ""}`}
        value={query}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showList}
        aria-controls={showList ? listId : undefined}
        aria-invalid={errorMessage ? true : undefined}
        aria-describedby={errorId}
        data-testid={testId}
      />
      {resolved && !showList && !hideSubline && !errorMessage && (
        <p className="mt-1 font-ui text-[13px] font-medium text-ink/80 leading-relaxed" data-testid={testId ? `${testId}-resolved` : undefined}>
          {resolved.flag ? `${resolved.flag} ` : ""}
          {citySubline(resolved)}
        </p>
      )}
      {errorMessage && !showList ? (
        <FormFieldError id={errorId} data-testid={testId ? `${testId}-error` : undefined}>
          {errorMessage}
        </FormFieldError>
      ) : null}
      {showList && (
        <div
          id={listId}
          className="absolute z-50 left-0 right-0 mt-1 rounded-card border border-black/[0.08] bg-white overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.08)]"
          data-testid={testId ? `${testId}-suggestions` : undefined}
          role="listbox"
        >
          {waiting && suggestions.length === 0 ? (
            <p className="px-3 py-2.5 font-ui text-[13px] font-medium text-ink/70">Searching cities…</p>
          ) : suggestions.length === 0 ? (
            <p className="px-3 py-2.5 font-ui text-[13px] font-medium text-ink/70">
              No matching cities.
            </p>
          ) : null}
          {suggestions.map((c) => (
            <button
              key={`${c.name}-${c.country_code || c.countryCode || ""}-${c.geoId || ""}`}
              type="button"
              role="option"
              aria-selected={resolved?.name === c.name}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(c)}
              className="block w-full text-left px-3 py-2 hover:bg-cream border-b border-black/[0.08] last:border-b-0 font-ui font-medium text-[14px]"
              data-testid={testId ? `${testId}-option-${c.name.replace(/\s+/g, "-").toLowerCase()}` : undefined}
            >
              <p className="font-semibold">
                {c.flag ? `${c.flag} ` : ""}
                {c.name}
              </p>
              <p className="font-ui text-[13px] font-medium text-ink/80 leading-relaxed">{citySubline(c)}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
